<template>
	<v-container>
		<!-- RESULT VIEW -->
		<div v-if="submitted && resultData">
			<div class="download-wrapper">
				<div class="download-dropdown-wrapper" v-click-outside="closeDropdown">
					<button class="download-btn" @click="toggleDropdown">
						<v-icon x-small left>fas fa-download</v-icon>
						Download VSME Document
						<v-icon x-small right style="margin-left:6px;">
							{{ showDownloadDropdown ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
						</v-icon>
					</button>
					<div v-if="showDownloadDropdown" class="download-dropdown-menu">
						<button class="dropdown-item" @click="handleDownloadPDF">
							<v-icon small left class="icon-pdf">fas fa-file-pdf</v-icon>
							As a PDF
						</button>
						<button class="dropdown-item" @click="handleDownloadJSON">
							<v-icon small left class="icon-json">fas fa-file-code</v-icon>
							As a JSON
						</button>
					</div>
				</div>
			</div>
			<v-card class="pa-6 mb-6" outlined>

				<div class="header-bar">
					<div>
						<h2>Health & Safety</h2>
					</div>

					<div style="display:flex; gap:8px; align-items:center;">
						<button class="btn-outline" @click="enableEdit">
							<v-icon x-small left>fas fa-pencil</v-icon>
							Edit
						</button>

						<span v-if="isDraft" class="draft-badge">
							<v-icon x-small style="margin-right:4px;">fas fa-clock</v-icon>
							Draft
						</span>
						<span v-else class="submitted-badge">
							<svg width="12" height="12" viewBox="0 0 12 12" fill="none" style="flex-shrink:0">
								<circle cx="6" cy="6" r="5.5" stroke="currentColor" stroke-width="1"/>
								<path d="M3.5 6L5.2 7.7L8.5 4.3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
							Submitted
						</span>
					</div>
				</div>

				<!-- 1. Governance & Policy -->
				<div class="section">
					<div class="section-title">1 · Governance & Policy</div>

					<div class="row-grid">
						<div class="row-item">
							<span class="row-key">Formal H&S policy?</span>
							<span class="row-val" v-html="badge(resultData.hasPolicy)"></span>
						</div>

						<div class="row-item">
							<span class="row-key">Dedicated H&S officer or team?</span>
							<span class="row-val" v-html="badge(resultData.hasOfficer)"></span>
						</div>

						<div class="row-item">
							<span class="row-key">Complies with safety regulations?</span>
							<span class="row-val" v-html="badge(resultData.regulationCompliance)"></span>
						</div>
					</div>
				</div>

				<!-- 2. Training & Prevention -->
				<div class="section">
					<div class="section-title">2 · Training & Prevention</div>

					<div class="row-grid">
						<div class="row-item">
							<span class="row-key">Employees receive H&S training?</span>
							<span class="row-val" v-html="badge(resultData.hasTraining)"></span>
						</div>

						<div class="row-item" v-if="resultData.hasTraining === 'Yes'">
							<span class="row-key">Training frequency</span>
							<span class="row-val">{{ fmt(resultData.trainingFrequency) }}</span>
						</div>

						<div class="row-item">
							<span class="row-key">Conducts workplace risk assessments?</span>
							<span class="row-val" v-html="badge(resultData.hasRiskAssessments)"></span>
						</div>

						<div class="row-item">
							<span class="row-key">Tracks near-miss incidents?</span>
							<span class="row-val" v-html="badge(resultData.tracksNearMiss)"></span>
						</div>
					</div>
				</div>

				<!-- 3. Incident Tracking -->
				<div class="section">
					<div class="section-title">3 · Incident Tracking</div>

					<div class="metric-grid">
						<div class="metric-card">
							<p class="metric-label">Workplace accidents</p>
							<p class="metric-value">{{ fmt(resultData.workplaceAccidents) }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Lost-time injuries</p>
							<p class="metric-value">{{ fmt(resultData.lostTimeInjuries) }}</p>
						</div>

						<div class="metric-card">
							<p class="metric-label">Work-related fatalities?</p>
							<p class="metric-value" v-html="badge(resultData.hasFatalities)"></p>
						</div>

						<div class="metric-card" v-if="resultData.hasFatalities === 'Yes'">
							<p class="metric-label">Number of fatalities</p>
							<p class="metric-value">{{ fmt(resultData.fatalitiesCount) }}</p>
						</div>
					</div>
				</div>

				<!-- 4. Safety Systems -->
				<div class="section">
					<div class="section-title">4 · Safety Systems</div>

					<div class="row-grid">
						<div class="row-item">
							<span class="row-key">Safety inspection frequency</span>
							<span class="row-val">{{ fmt(resultData.inspectionFrequency) }}</span>
						</div>

						<div class="row-item">
							<span class="row-key">Emergency response procedures?</span>
							<span class="row-val" v-html="badge(resultData.hasEmergencyProcedures)"></span>
						</div>
					</div>
				</div>

			</v-card>
		</div>

		<!-- FORM VIEW -->
		<v-form v-else ref="form" v-model="formIsValid">
			<!-- Draft banner -->
			<v-alert
				v-if="isDraft"
				type="warning"
				dense
				outlined
				class="mb-3"
				icon="fas fa-clock"
			>
				You have a saved draft. Complete and submit when ready.
			</v-alert>
			<v-card class="pa-3 mb-6" outlined>
				<v-col cols="12" class="d-flex justify-center"><h2>HEALTH & SAFETY</h2></v-col>
				<!-- 1. Governance & Policy -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>1. Governance & Policy</h2></v-col>

					<v-col cols="12">
						<v-select
							v-model="form.hasPolicy"
							:items="['Yes', 'No']"
							label="Q1. Do you have a formal Health & Safety policy?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q1')">
							{{ showDesc.q1 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q1" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-1, paragraph 13: The undertaking shall disclose policies related to occupational health and safety covering its own workforce.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.hasOfficer"
							:items="['Yes', 'No']"
							label="Q2. Is there a dedicated Health & Safety officer or team?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q2')">
							{{ showDesc.q2 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q2" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-2, paragraph 16: The undertaking shall disclose governance structures and assigned responsibilities for health and safety management.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.regulationCompliance"
							:items="['Yes', 'No', 'Partially']"
							label="Q3. Do you comply with local workplace safety regulations?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q3')">
							{{ showDesc.q3 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q3" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-3, paragraph 17: The undertaking shall disclose compliance with applicable occupational health and safety laws and regulations.)
						</div>
					</v-col>
				</v-row>

				<!-- 2. Training & Prevention -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>2. Training & Prevention</h2></v-col>

					<v-col cols="12">
						<v-select
							v-model="form.hasTraining"
							:items="['Yes', 'No']"
							label="Q4. Do employees receive regular health & safety training?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" md="6" v-if="form.hasTraining === 'Yes'">
						<v-text-field
							v-model="form.trainingFrequency"
							label="Frequency (e.g. Monthly, Quarterly, Annually)"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q4')">
							{{ showDesc.q4 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q4" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-4, paragraph 18: The undertaking shall disclose health and safety training provided to employees including frequency and scope.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.hasRiskAssessments"
							:items="['Yes', 'No']"
							label="Q5. Do you conduct workplace risk assessments?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q5')">
							{{ showDesc.q5 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q5" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-5, paragraph 20: The undertaking shall disclose workplace hazard identification and risk assessment processes.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.tracksNearMiss"
							:items="['Yes', 'No']"
							label="Q6. Do you track near-miss incidents?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q6')">
							{{ showDesc.q6 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q6" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-6, paragraph 21: The undertaking shall disclose systems for tracking and recording near-miss incidents for preventive action.)
						</div>
					</v-col>
				</v-row>

				<!-- 3. Incident Tracking -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>3. Incident Tracking</h2></v-col>

					<v-col cols="12">
						<v-text-field
							v-model="form.workplaceAccidents"
							label="Q7. Number of workplace accidents in last reporting period"
							type="number"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q7')">
							{{ showDesc.q7 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q7" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-7, paragraph 22: The undertaking shall disclose the number of work-related accidents occurring during the reporting period.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-text-field
							v-model="form.lostTimeInjuries"
							label="Q8. Number of lost-time injuries"
							type="number"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q8')">
							{{ showDesc.q8 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q8" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-7, paragraph 23: The undertaking shall disclose lost-time injury frequency and severity rates where applicable.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.hasFatalities"
							:items="['Yes', 'No']"
							label="Q9. Were there any work-related fatalities?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" md="6" v-if="form.hasFatalities === 'Yes'">
						<v-text-field
							v-model="form.fatalitiesCount"
							label="Number of fatalities"
							type="number"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q9')">
							{{ showDesc.q9 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q9" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-7, paragraph 24: The undertaking shall disclose any work-related fatalities occurring during the reporting period.)
						</div>
					</v-col>
				</v-row>

				<!-- 4. Safety Systems -->
				<v-row class="contentRow">
					<v-col cols="12"><h2>4. Safety Systems</h2></v-col>

					<v-col cols="12">
						<v-select
							v-model="form.inspectionFrequency"
							:items="['Monthly', 'Quarterly', 'Annually', 'Not regularly']"
							label="Q10. How often are safety inspections conducted?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q10')">
							{{ showDesc.q10 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q10" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-8, paragraph 25: The undertaking shall disclose the frequency and scope of workplace health and safety inspections.)
						</div>
					</v-col>

					<v-col cols="12">
						<v-select
							v-model="form.hasEmergencyProcedures"
							:items="['Yes', 'No']"
							label="Q11. Do you have emergency response procedures in place?"
							filled
							dense
						/>
					</v-col>
					<v-col cols="12" class="desc-wrapper">
						<v-btn text small color="primary" @click="toggleDesc('q11')">
							{{ showDesc.q11 ? 'Hide description' : 'Show description' }}
							<v-icon x-small class="icon-circle ml-2">fas fa-section</v-icon>
						</v-btn>
						<div v-if="showDesc.q11" class="grey--text text--darken-1 mt-1 text-caption">
							(ESRS S1-9, paragraph 26: The undertaking shall disclose emergency preparedness and response procedures for workplace incidents.)
						</div>
					</v-col>
				</v-row>

				<!-- Error -->
				<v-row class="contentRow" v-if="errorMessage">
					<v-col cols="12">
						<v-alert type="error" dense>{{ errorMessage }}</v-alert>
					</v-col>
				</v-row>

				<!-- Actions -->
				<v-row class="contentRow">
					<v-col cols="12" class="d-flex justify-center">
						<v-btn outlined color="primary" class="mr-5" :loading="loading" :disabled="loading" @click="saveDraft">
							<v-icon x-small left>fas fa-floppy-disk</v-icon>
							Save as Draft
						</v-btn>
						<v-btn color="primary" :loading="loading" :disabled="loading" @click="submit">
							Submit
						</v-btn>
					</v-col>
				</v-row>
			</v-card>
		</v-form>
	</v-container>
</template>

<script>
import CsrAPI from "../api/csr/csr"

export default {
	props: {
		initialData: { type: Object, default: null },
		isEditMode:  { type: Boolean, default: false },
	},

	data() {
		return {
			formIsValid: false,
			loading: false,
			errorMessage: "",
			showDesc: {},
			form: {
				hasPolicy: null,
				hasOfficer: null,
				regulationCompliance: null,
				hasTraining: null,
				trainingFrequency: "",
				hasRiskAssessments: null,
				tracksNearMiss: null,
				workplaceAccidents: null,
				lostTimeInjuries: null,
				hasFatalities: null,
				fatalitiesCount: null,
				inspectionFrequency: null,
				hasEmergencyProcedures: null,
				isDraft: false,
			},
			submitted: false,
			resultData: null,
			isDraft: false,
			hasExistingRecord: false,
			showDownloadDropdown: false,
		}
	},

	watch: {
		initialData: {
			immediate: true,
			handler(res) {
				if (!res) return
				this.mapDataToForm(res)
			},
		},
	},

	async created() {
		this.loadData()
	},

	methods: {
		toggleDesc(key) {
			this.$set(this.showDesc, key, !this.showDesc[key])
		},

		enableEdit() {
			this.submitted = false
		},

		async loadData() {
			try {
				const res = await CsrAPI.getVsmeHealth()

				if (!res || !res.Has_Policy) {
					this.submitted = false
					return
				}

				this.hasExistingRecord = true
				this.mapDataToForm(res)
				this.resultData = { ...this.form }

				if (res.Is_Draft === true || res.Is_Draft === 1) {
					// Draft exists — reopen the form pre-filled, don't show result view
					this.isDraft = true
					this.submitted = false
				} else if (!this.isEditMode) {
					this.submitted = true
				}

			} catch (error) {
				console.error("Failed to load health & safety data:", error)
			}
		},

		mapDataToForm(res) {
			this.form = {
				...this.form,
				hasPolicy:              res.Has_Policy              || null,
				hasOfficer:             res.Has_Officer             || null,
				regulationCompliance:   res.Regulation_Compliance   || null,
				hasTraining:            res.Has_Training            || null,
				trainingFrequency:      res.Training_Frequency      || "",
				hasRiskAssessments:     res.Has_Risk_Assessments    || null,
				tracksNearMiss:         res.Tracks_Near_Miss        || null,
				workplaceAccidents:     res.Workplace_Accidents     ?? null,
				lostTimeInjuries:       res.Lost_Time_Injuries      ?? null,
				hasFatalities:          res.Has_Fatalities          || null,
				fatalitiesCount:        res.Fatalities_Count        ?? null,
				inspectionFrequency:    res.Inspection_Frequency    || null,
				hasEmergencyProcedures: res.Has_Emergency_Procedures || null,
			}
		},

		async _saveOrUpdate(isDraft) {
			const f = this.form
			const args = [
				f.hasPolicy,
				f.hasOfficer,
				f.regulationCompliance,
				f.hasTraining,
				f.trainingFrequency,
				f.hasRiskAssessments,
				f.tracksNearMiss,
				f.workplaceAccidents,
				f.lostTimeInjuries,
				f.hasFatalities,
				f.fatalitiesCount,
				f.inspectionFrequency,
				f.hasEmergencyProcedures,
				isDraft,
			]
			if (this.hasExistingRecord) {
				await CsrAPI.updateVsmeHealth(...args)
			} else {
				await CsrAPI.saveVsmeHealth(...args)
				this.hasExistingRecord = true   // flip after first insert
			}
		},

		async submit() {
			this.loading = true
			this.errorMessage = ""
			try {
				await this._saveOrUpdate(false)
				this.resultData = { ...this.form }
				this.isDraft = false
				this.submitted = true
				this.$emit("saved")
			} catch (error) {
				this.errorMessage = error.message
			} finally {
				this.loading = false
			}
		},

		async saveDraft() {
			this.loading = true
			this.errorMessage = ""
			try {
				await this._saveOrUpdate(true)
				this.isDraft = true
				this.$emit("drafted")
			} catch (error) {
				this.errorMessage = error.message
			} finally {
				this.loading = false
			}
		},

		toggleDropdown() {
			this.showDownloadDropdown = !this.showDownloadDropdown
		},

		closeDropdown() {
			this.showDownloadDropdown = false
		},

		handleDownloadPDF() {
			this.showDownloadDropdown = false
			CsrAPI.downloadVsmePDF()
		},
		
		handleDownloadJSON() {
			this.showDownloadDropdown = false
			CsrAPI.downloadJSON()
		},

		fmt(val, suffix = "") {
			if (val === null || val === undefined || val === "") {
				return "—"
			}
			return val + suffix
		},

		badge(val) {
			if (val === "Yes") {
				return "<span class='yes-badge'>Yes</span>"
			}
			if (val === "No") {
				return "<span class='no-badge'>No</span>"
			}
			if (val === "Partially") {
				return "<span class='partial-badge'>Partially</span>"
			}
			return val || "—"
		},
	},
}
</script>

<style scoped>
.esgcontainer h1 {
  color: #1e7c83;
  font-weight: bold;
}

h2 {
  color: #1e7c83;
}

h3 {
  color: #424242;
  margin-bottom: 8px;
}

.contentRow {
  margin-bottom: 16px;
}

.icon-circle {
	border: 1px solid currentColor;
	border-radius: 50%;
	padding: 4px;
}

.desc-wrapper {
	padding-top: 0 !important;
	margin-top: -20px;
}

.header-bar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 1.25rem;
}

.submitted-badge {
	display: flex;
	align-items: center;
	gap: 5px;
	background: #e8f5e9;
	color: #2e7d32;
	padding: 4px 12px;
	border-radius: 20px;
}

.btn-outline {
	border: 1px solid #bdbdbd;
	background: none;
	border-radius: 6px;
	padding: 5px 12px;
	color: #424242;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 4px;
}

.btn-outline:hover {
	background: #f5f5f5;
}

.download-wrapper {
	display: flex;
	justify-content: flex-end;
	margin-bottom: 10px; 
}

.download-btn {
	border: 1px solid #bdbdbd;
	background: none;
	border-radius: 6px;
	padding: 5px 12px;
	color: #424242;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 4px;
}

.download-dropdown-wrapper {
    position: relative;
    display: inline-block;
}

.download-dropdown-menu {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.10);
    min-width: 160px;
    z-index: 100;
    overflow: hidden;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 16px;
    background: none;
    border: none;
    color: #424242;
    cursor: pointer;
    text-align: left;
    font-size: 0.875rem;
    transition: background 0.15s;
}

.dropdown-item:hover {
    background: #f5f5f5;
}

.icon-pdf {
    color: #e53935 !important;  
}

.icon-json {
    color: #f9a825 !important;
}

.section {
	background: #fff;
	border: 1px solid #2d9b9b;
	border-radius: 10px;
	padding: 1.25rem 1.5rem;
	margin-bottom: 1rem;
}

.section-title {
	color: #9e9e9e;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	margin: 0 0 1rem;
}

.metric-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
	gap: 10px;
	margin-bottom: 0.5rem;
}

.metric-card {
	background: #f5f5f5;
	border-radius: 8px;
	padding: 0.75rem 1rem;
}

.metric-label {
	color: #757575;
	margin: 0 0 4px;
}

.metric-value {
	color: #212121;
	margin: 0;
}

.row-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
	gap: 12px;
}

.row-item {
	display: flex;
	flex-direction: column;
	gap: 3px;
}

.row-key {
	color: #9e9e9e;
}

.row-val {
	color: #212121;
}

.tag-list {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
}

.tag {
	background: #e0f2f1;
	color: #00695c;
	padding: 3px 10px;
	border-radius: 20px;
}

.empty-note {
	color: #bdbdbd;
}

.yes-badge {
	background: #e8f5e9;
	color: #2e7d32;
	padding: 2px 9px;
	border-radius: 20px;
	display: inline-block;
}

.no-badge {
	background: #f5f5f5;
	color: #757575;
	padding: 2px 9px;
	border-radius: 20px;
	display: inline-block;
}

.partial-badge {
	background: #fff8e1;
	color: #f57f17;
	padding: 2px 9px;
	border-radius: 20px;
	display: inline-block;
}

.text-block {
	background: #f9f9f9;
	border-radius: 6px;
	padding: 10px 14px;
}

.text-body {
	color: #424242;
	line-height: 1.6;
}

.sub-label {
	color: #9e9e9e;
	margin-bottom: 8px;
}

.divider {
	border: none;
	border-top: 0.5px solid #e0e0e0;
	margin: 1rem 0;
}

.draft-badge {
    display: flex;
    align-items: center;
    gap: 5px;
    background: #fff8e1;
    color: #f57f17;
    padding: 4px 12px;
    border-radius: 20px;
}

@media print {
	.btn-outline { display: none !important; }
}
</style>